import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Star, Trash2 } from 'lucide-react';
import { listReviewsAdmin, setReviewPublished, deleteReviewAdmin, type Review } from '../../lib/reviews';
import { EmptyState, Panel, PanelHead, Stamp } from '../../components/lms/ui';
import { ListRowsSkeleton } from '../../components/lms/Skeletons';

const ReviewsAdmin: React.FC = () => {
  const [items, setItems] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listReviewsAdmin().catch(() => []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const toggle = async (r: Review) => {
    setBusy(r.id);
    setError('');
    try {
      await setReviewPublished(r.id, !r.is_published);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the review state.');
    } finally {
      setBusy('');
    }
  };

  const remove = async (r: Review) => {
    if (!confirm('Delete this review permanently?')) return;
    setBusy(r.id);
    setError('');
    try {
      await deleteReviewAdmin(r.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the review.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mt-6 space-y-4">
      {error && (
        <p className="border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>
      )}

      <Panel>
        <PanelHead
          title="Review moderation"
          action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{items.length} on record</span>}
        />
        <p className="border-b border-rule px-5 py-3 text-[12px] leading-5 text-ink-2">
          Hidden reviews disappear from programme pages; deleting removes the record permanently. Only enrolled students can submit a review.
        </p>

        {loading ? (
          <ListRowsSkeleton rows={3} label="Loading reviews" />
        ) : items.length === 0 ? (
          <EmptyState title="No reviews on record" body="Student reviews appear here after they are submitted from a programme page. Nothing needs moderation yet." />
        ) : (
          <ul className="divide-y divide-rule">
            {items.map((r) => (
              <li key={r.id} className="px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-0.5" aria-label={`${r.rating} out of 5`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`h-3.5 w-3.5 ${n <= r.rating ? 'text-manila' : 'text-rule'}`}
                            fill={n <= r.rating ? 'currentColor' : 'none'}
                            strokeWidth={1.5}
                          />
                        ))}
                      </span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                        Course {r.course_id} · User {r.user_id.slice(0, 8)} · {new Date(r.created_at).toLocaleDateString('en-NG')}
                      </span>
                      <Stamp tone={r.is_published ? 'cleared' : 'neutral'}>{r.is_published ? 'Published' : 'Hidden'}</Stamp>
                    </div>
                    {r.title && <p className="mt-2 text-[13.5px] font-semibold leading-snug text-ink">{r.title}</p>}
                    <p className="mt-1 max-w-[80ch] whitespace-pre-wrap text-[12.5px] leading-5 text-ink-2">
                      {r.body || 'No written review.'}
                    </p>
                  </div>
                  <div className="flex flex-none items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void toggle(r)}
                      disabled={busy === r.id}
                      className="lms-btn-secondary !px-2.5 !py-1.5 !text-[11px]"
                    >
                      {r.is_published ? <EyeOff className="h-3 w-3" strokeWidth={1.5} /> : <Eye className="h-3 w-3" strokeWidth={1.5} />}
                      {r.is_published ? 'Hide' : 'Publish'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(r)}
                      disabled={busy === r.id}
                      className="lms-btn-danger !px-2.5 !py-1.5 !text-[11px]"
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};

export default ReviewsAdmin;
